import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { navigateToSettingsHome } from "./settingsExit";
import { Check, Save } from "lucide-react";
import { useToast } from "../../Moduels/Context/ToastContext";
import { fetchCompOffCalendar, fetchCompOffSettings, saveCompOffSettings } from "../../Moduels/leave/compOffApi";
import {
  EMPTY_COMP_OFF_FORM,
  formFromSettings,
  payloadFromForm,
  validateCompOffForm,
} from "./compOffSettingsModel";
import "./CompOffSettings.css";

const HOUR_OPTIONS = [
  { value: "full_shift", label: "Full Day (As per Shift)" },
  { value: "hours_4", label: "4 Hours" },
  { value: "hours_6", label: "6 Hours" },
  { value: "hours_8", label: "8 Hours" },
  { value: "custom", label: "Custom Hours" },
];

const CREDIT_OPTIONS = [
  { value: "0.5", label: "0.5 Day" },
  { value: "1", label: "1 Day" },
  { value: "1.5", label: "1.5 Days" },
  { value: "2", label: "2 Days" },
];

const EXPIRY_OPTIONS = [
  { value: "never", label: "Never expires" },
  { value: "after_days", label: "After certain days" },
  { value: "financial_year", label: "End of financial year" },
  { value: "calendar_year", label: "End of calendar year" },
  { value: "custom_date", label: "Custom date" },
];

function tidyValue(value) {
  if (!value) return "—";
  return String(value)
    .replace(/(\d+)\.00\b/g, "$1")
    .replace(/(\d+\.\d)0\b/g, "$1");
}

function apiMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || String(item)).join(" ");
  }
  return fallback;
}

export default function CompOffSettingsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_COMP_OFF_FORM);
  const [saved, setSaved] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [savedFlash, setSavedFlash] = useState(null);
  const successRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [settings, cal] = await Promise.all([
        fetchCompOffSettings(),
        fetchCompOffCalendar().catch(() => null),
      ]);
      setSaved(settings);
      setForm(formFromSettings(settings));
      setCalendar(cal);
      setDirty(false);
    } catch (err) {
      setLoadError(apiMessage(err, "Comp Off settings could not be loaded. You can try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onLeave = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  const patch = (partial) => {
    setForm((current) => ({ ...current, ...partial }));
    setDirty(true);
    setSavedFlash(null);
  };

  const locked = !form.enabled;
  const summary = saved?.summary;

  const calendarNote = useMemo(() => {
    if (!calendar) return "";
    if (!calendar.configured) return calendar.message;
    const offs = (calendar.weekly_offs || []).join(", ") || "no fixed weekly off";
    return `${calendar.holiday_count} holiday${calendar.holiday_count === 1 ? "" : "s"} this year. Weekly off: ${offs}.`;
  }, [calendar]);

  useEffect(() => {
    if (!savedFlash) return undefined;
    successRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    return undefined;
  }, [savedFlash]);

  const savedFacts = useMemo(() => {
    if (!savedFlash) return [];
    return [
      ["Status", savedFlash.status || "Saved"],
      ["Applicable days", savedFlash.applicable_days || "—"],
      ["Generation", savedFlash.generation_method || "—"],
      ["Minimum hours", savedFlash.minimum_working_hours || "—"],
      ["Credit", tidyValue(savedFlash.credit_value)],
      ["Maximum", tidyValue(savedFlash.maximum_balance)],
      ["Expiry", savedFlash.expiry || "—"],
      ["Approval", savedFlash.approval_required || "—"],
    ];
  }, [savedFlash]);

  const onSave = async () => {
    const nextErrors = validateCompOffForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setSaving(true);
    try {
      const updated = await saveCompOffSettings(payloadFromForm(form));
      setSaved(updated);
      setForm(formFromSettings(updated));
      setDirty(false);
      setErrors({});
      setSavedFlash(updated?.summary || { status: form.enabled ? "Enabled" : "Disabled" });
      toast?.success?.("Comp Off settings submitted successfully.");
      navigateToSettingsHome(navigate, { replace: true });
    } catch (err) {
      toast?.error?.(apiMessage(err, "Settings could not be saved. Your edits are still on this page."));
      setErrors((current) => ({
        ...current,
        form: apiMessage(err, "Settings could not be saved. Your edits are still on this page."),
      }));
    } finally {
      setSaving(false);
    }
  };

  const storedFacts = summary
    ? [
        ["Status", summary.status],
        ["Applicable days", summary.applicable_days],
        ["Generation", summary.generation_method],
        ["Minimum hours", summary.minimum_working_hours],
        ["Credit", tidyValue(summary.credit_value)],
        ["Maximum", tidyValue(summary.maximum_balance)],
        ["Expiry", summary.expiry],
        ["Approval", summary.approval_required],
      ]
    : [];

  return (
    <div className="co-page">
      {loading ? <p className="co-state">Loading Comp Off settings…</p> : null}
      {loadError ? (
        <div className="co-banner" role="alert">
          <span>{loadError}</span>
          <button type="button" onClick={load}>Try again</button>
        </div>
      ) : null}
      {errors.form ? <p className="co-banner" role="alert">{errors.form}</p> : null}

      {savedFlash ? (
        <section className="co-success" role="status" ref={successRef}>
          <span className="co-success-mark" aria-hidden="true"><Check size={16} /></span>
          <div className="co-success-body">
            <strong>Comp Off settings submitted successfully</strong>
            <p>The policy is stored for this organisation and applies to future qualifying attendance.</p>
            <dl className="co-success-grid">
              {savedFacts.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <button type="button" className="co-success-dismiss" onClick={() => setSavedFlash(null)}>Dismiss</button>
        </section>
      ) : null}

      {!loading && !loadError ? (
        <div className="co-stack">
        <section className="co-card" aria-label="Comp Off policy">
          <header className="co-card-head">
            <h2>Policy</h2>
            <p>Turn Comp Off on and choose which days can earn a credit.</p>
          </header>
          <div className="co-row">
            <div className="co-copy">
              <h2>Enable Comp Off</h2>
              <p>Turn this on to grant credits when employees work an eligible holiday or weekly off.</p>
            </div>
            <div className="co-control">
              <label className="co-switch">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(event) => patch({ enabled: event.target.checked })}
                />
                <span className="co-switch-ui" />
                <span>{form.enabled ? "Enabled" : "Disabled"}</span>
              </label>
            </div>
          </div>

          <fieldset disabled={locked}>
            <div className="co-row">
              <div className="co-copy">
                <h2>Applicable Days</h2>
                <p>Choose which days can earn Comp Off. A normal working day never qualifies.</p>
                {calendarNote ? <p className="co-note">{calendarNote}</p> : null}
                <p className="co-links">
                  <Link to="/holiday-calendar">Holiday calendar</Link>
                  <Link to="/settings/attendance-leave-setup?tab=weekly-off">Weekly off</Link>
                </p>
                {errors.applicable_days ? <p className="co-error">{errors.applicable_days}</p> : null}
              </div>
              <div className="co-control">
                <div className="co-checks">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.holidays}
                      onChange={(event) => patch({ holidays: event.target.checked })}
                    />
                    Holidays
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={form.weeklyOffs}
                      onChange={(event) => patch({ weeklyOffs: event.target.checked })}
                    />
                    Weekly Offs
                  </label>
                </div>
              </div>
            </div>

            <div className="co-row">
              <div className="co-copy">
                <h2>Generation Method</h2>
                <p>
                  {form.generation_method === "automatic"
                    ? "A qualifying attendance record creates the credit automatically."
                    : "HR or a manager reviews the attendance and approves or rejects the credit."}
                </p>
              </div>
              <div className="co-control">
                <label className="co-field">
                  <span className="co-sr">Generation method</span>
                  <select
                    value={form.generation_method}
                    onChange={(event) => patch({ generation_method: event.target.value })}
                  >
                    <option value="automatic">Automatic</option>
                    <option value="manual">Manual</option>
                  </select>
                </label>
              </div>
            </div>
          </fieldset>
        </section>

        <section className="co-card" aria-label="Credit rules">
          <header className="co-card-head">
            <h2>Credit rules</h2>
            <p>How many hours qualify, and how many days are credited.</p>
          </header>
          <fieldset disabled={locked}>
            <div className="co-row">
              <div className="co-copy">
                <h2>Minimum Working Hours</h2>
                <p>Full day uses the employee’s shift length. A credit is not granted when the hours cannot be verified.</p>
                {errors.custom_hours ? <p className="co-error">{errors.custom_hours}</p> : null}
              </div>
              <div className="co-control">
                <label className="co-field">
                  <span className="co-sr">Minimum working hours</span>
                  <select
                    value={form.min_hours_mode}
                    onChange={(event) => patch({ min_hours_mode: event.target.value })}
                  >
                    {HOUR_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                {form.min_hours_mode === "custom" ? (
                  <label className="co-unit">
                    <input
                      type="number"
                      min="0.5"
                      max="24"
                      step="0.5"
                      aria-label="Custom hours"
                      value={form.custom_hours}
                      onChange={(event) => patch({ custom_hours: event.target.value })}
                    />
                    <span>hours</span>
                  </label>
                ) : null}
              </div>
            </div>

            <div className="co-row">
              <div className="co-copy">
                <h2>Credit Value</h2>
                <p>Days credited for each qualifying attendance day.</p>
              </div>
              <div className="co-control">
                <label className="co-field">
                  <span className="co-sr">Credit value</span>
                  <select
                    value={form.credit_value}
                    onChange={(event) => patch({ credit_value: event.target.value })}
                  >
                    {CREDIT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="co-row">
              <div className="co-copy">
                <h2>Maximum Balance</h2>
                <p>An employee cannot hold more than this many Comp Off days. Extra qualifying days stay for review.</p>
                {errors.max_balance ? <p className="co-error">{errors.max_balance}</p> : null}
              </div>
              <div className="co-control">
                <label className="co-unit">
                  <input
                    type="number"
                    min="0.5"
                    max="365"
                    step="0.5"
                    aria-label="Maximum Comp Off balance"
                    value={form.max_balance}
                    onChange={(event) => patch({ max_balance: event.target.value })}
                  />
                  <span>days</span>
                </label>
              </div>
            </div>
          </fieldset>
        </section>

        <section className="co-card" aria-label="Expiry and approval">
          <header className="co-card-head">
            <h2>Expiry and approval</h2>
            <p>When a credit ends, and whether it waits for a review.</p>
          </header>
          <fieldset disabled={locked}>
            <div className="co-row">
              <div className="co-copy">
                <h2>Expiry</h2>
                <p>A credit stays usable through the end of its expiry date. Later policy edits do not change credits already granted.</p>
                {errors.expiry_days ? <p className="co-error">{errors.expiry_days}</p> : null}
                {errors.custom_expiry_date ? <p className="co-error">{errors.custom_expiry_date}</p> : null}
              </div>
              <div className="co-control">
                <label className="co-field">
                  <span className="co-sr">Expiry rule</span>
                  <select
                    value={form.expiry_mode}
                    onChange={(event) => patch({ expiry_mode: event.target.value })}
                  >
                    {EXPIRY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                {form.expiry_mode === "after_days" ? (
                  <label className="co-unit">
                    <input
                      type="number"
                      min="1"
                      max="3650"
                      aria-label="Expiry days"
                      value={form.expiry_days}
                      onChange={(event) => patch({ expiry_days: event.target.value })}
                    />
                    <span>days</span>
                  </label>
                ) : null}
                {form.expiry_mode === "custom_date" ? (
                  <input
                    type="date"
                    aria-label="Custom expiry date"
                    value={form.custom_expiry_date}
                    onChange={(event) => patch({ custom_expiry_date: event.target.value })}
                  />
                ) : null}
              </div>
            </div>

            <div className="co-row">
              <div className="co-copy">
                <h2>Expiry Reminder</h2>
                <p>Days before expiry to remind the employee. Use 0 to turn reminders off.</p>
                {errors.reminder_days ? <p className="co-error">{errors.reminder_days}</p> : null}
              </div>
              <div className="co-control">
                <label className="co-unit">
                  <input
                    type="number"
                    min="0"
                    max="90"
                    aria-label="Expiry reminder days"
                    value={form.reminder_days}
                    onChange={(event) => patch({ reminder_days: event.target.value })}
                  />
                  <span>days before</span>
                </label>
              </div>
            </div>

            <div className="co-row">
              <div className="co-copy">
                <h2>Require Approval</h2>
                <p>Applies to automatic mode. Yes creates a pending credit until HR or a manager approves it.</p>
              </div>
              <div className="co-control">
                <label className="co-field">
                  <span className="co-sr">Approval requirement</span>
                  <select
                    value={form.approval_required ? "yes" : "no"}
                    onChange={(event) => patch({ approval_required: event.target.value === "yes" })}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </label>
              </div>
            </div>
          </fieldset>
        </section>

        {storedFacts.length ? (
          <section className="co-card co-saved" aria-live="polite">
            <header className="co-card-head">
              <h2>Saved configuration</h2>
              <p>The policy stored for this organisation. It updates after a successful submit.</p>
            </header>
            <div className="co-metrics">
              {storedFacts.map(([label, value]) => (
                <div className="co-metric" key={label}>
                  <span>{label}</span>
                  {label === "Status" ? (
                    <strong>
                      <em className={value === "Enabled" ? "co-badge co-badge-on" : "co-badge"}>{value}</em>
                    </strong>
                  ) : (
                    <strong>{value}</strong>
                  )}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <div className="co-actions">
          <p>{dirty ? "You have unsaved changes. Submit to store this policy." : "Use Save Settings to store this policy for the organisation."}</p>
          <button type="button" className="co-save" onClick={onSave} disabled={saving || loading}>
            <Save size={16} aria-hidden="true" />
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
        </div>
      ) : null}
    </div>
  );
}
